import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { LoadingIndicatorProgress } from '@vindr/ui';
import { usePrint } from '../../contextProviders';
import { eventTarget } from '@cornerstonejs/core';
import './ViewportGrid.css';

const BASE_PRINT = 3000;
const CHANGE_SERIES_EVENT = 'CHANGE_SERIES_EVENT';
const MPR_ORTHOGONAL_APPLYING_EVENT = 'event::mpr_orthogonal_applying_event';
const MPR_ORTHOGONAL_APPLIED_EVENT = 'event::mpr_orthogonal_applied_event';

function ViewportGrid({
  numRows,
  numCols,
  layoutType,
  children,
  viewportType,
  activePatientInfo
}) {
  const [currentViewportType, setViewportType] = useState('default');
  const [isChanging, setIsChanging] = useState(null);
  const [{ isPrintEnabled, isPrinting, ratio, orientation }] = usePrint();
  const printRatio = orientation === 'PORTRAIT' ? ratio : 1 / ratio;

  useEffect(() => {
    if (viewportType !== currentViewportType) {
      setTimeout(() => {
        setViewportType(viewportType);
      }, 1000);
    } else if (viewportType === 'vr') {
      setViewportType(null);
      setTimeout(() => {
        setViewportType(viewportType);
      }, 1000);
    } else {
      setViewportType(viewportType);
    }
  }, [viewportType]);

  useEffect(() => {
    const changeSeriesHandle = () => {
      if (currentViewportType !== 'default') {
        setIsChanging(true);
        setTimeout(() => {
          setIsChanging(false);
        }, 1000);
      }
    };

    const mprOrthogonalApplyingHandle = () => {
      setIsChanging(true);
    }

    const mprOrthogonalAppliedHandle = () => {
      setIsChanging(false);
    }

    eventTarget.addEventListener(CHANGE_SERIES_EVENT, changeSeriesHandle);
    eventTarget.addEventListener(MPR_ORTHOGONAL_APPLYING_EVENT, mprOrthogonalApplyingHandle);
    eventTarget.addEventListener(MPR_ORTHOGONAL_APPLIED_EVENT, mprOrthogonalAppliedHandle);

    return () => {
      eventTarget.removeEventListener(CHANGE_SERIES_EVENT, changeSeriesHandle);
      eventTarget.removeEventListener(MPR_ORTHOGONAL_APPLYING_EVENT, mprOrthogonalApplyingHandle);
      eventTarget.removeEventListener(MPR_ORTHOGONAL_APPLIED_EVENT, mprOrthogonalAppliedHandle);
    };
  }, [currentViewportType]);

  let height = window.innerHeight - 40; // 40 is height of header
  let width = window.innerWidth - 440; // 440 is width of left sidebar and print form

  if (printRatio < width / height) {
    width = printRatio * height;
  } else {
    height = width / printRatio;
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-between">
      {!isPrintEnabled && activePatientInfo && (
        <div className="h-[30px] w-full">{activePatientInfo()}</div>
      )}
      <div
        className={`flex h-full w-full flex-1 items-center justify-center ${
          isPrintEnabled || isPrinting ? 'bg-black' : ''
        }`}
        style={
          isPrinting
            ? {
                position: 'absolute',
                left: 0,
                top: 0,
              }
            : {}
        }
      >
        <div
          data-cy="viewport-grid"
          className="viewport-grid bg-black"
          style={{
            position: 'relative',
            flexShrink: 0,
            height: isPrinting
              ? BASE_PRINT * (height / width)
              : isPrintEnabled
              ? height
              : '100%',
            width: isPrinting ? BASE_PRINT : isPrintEnabled ? width : '100%',
          }}
        >
          {children}
        </div>
      </div>
      {(currentViewportType !== viewportType || isChanging || isPrinting) && (
        <LoadingIndicatorProgress className="h-full w-full bg-black" />
      )}
    </div>
  );
}

ViewportGrid.propTypes = {
  /** Number of columns */
  numRows: PropTypes.number.isRequired,
  /** Number of rows */
  numCols: PropTypes.number.isRequired,
  layoutType: PropTypes.string,
  /** Array of React Components to render within grid */
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
};

export default ViewportGrid;
